import React, { useCallback, useEffect, useMemo, useReducer, useRef } from 'react'
import isFunction from 'lodash/isFunction'

import { isEmpty } from '@/shared/utils'

import type {
  FormActions,
  FormErrors,
  FormSetErrorHandler,
  FormState,
  NotEmpty,
  StateRecord,
  UseFormOptions,
  UseFormRegister,
  UseFormResult,
  UseFormSubmitHandler,
  UseFormValidators,
  UseFormValue,
} from './use-form.types'

const isEmptyError = (errors: any) => isEmpty(errors) || Object.values(errors)?.every((v) => !v)

function createInitialState<R extends StateRecord>(initialState: FormState<R>): FormState<R> {
  return {
    values: initialState.values as NotEmpty<R>,
    errors: initialState.errors,
    isValidating: false,
  }
}

function formReducer<R extends StateRecord>(
  state: FormState<R>,
  action: FormActions<R>,
  validate?: UseFormValidators<R>
): FormState<R> {
  switch (action.type) {
    case 'update': {
      return {
        ...state,
        values: {
          ...state.values,
          [action.payload.field]: action.payload.value,
        },
      }
    }
    case 'update-values': {
      return {
        ...state,
        values: action.payload,
      }
    }
    case 'update-errors': {
      return {
        ...state,
        errors: action.payload,
      }
    }
    case 'clear-errors': {
      const { field } = action.payload
      let updatedErrors = state.errors

      if (!!updatedErrors && !!field) {
        const { [field]: _, ...rest } = updatedErrors
        updatedErrors = (isEmpty(rest) ? undefined : rest) as any
      } else {
        updatedErrors = undefined
      }

      return {
        ...state,
        errors: updatedErrors,
      }
    }
    case 'set-validating':
      return {
        ...state,
        isValidating: action.payload,
      }
    case 'reset': {
      const { defaultValues, field } = action.payload

      if (field) {
        const { [field]: _value, ...otherValues } = state.values
        const { [field]: _error, ...otherErrors } = state.errors || {}

        return {
          values: { ...otherValues, [field]: defaultValues[field] } as R,
          errors: isEmpty(otherErrors) ? undefined : ({ ...otherErrors } as FormErrors<R>),
          isValidating: false,
        }
      }

      return {
        values: defaultValues,
        errors: undefined,
        isValidating: false,
      }
    }
    default:
      return state
  }
}

/**
 * A custom hook to handle form processing.
 *
 * Parameters:
 * - Provide `defaultValues` to initiate form values processing, note that it cannot be empty. We can also use generic for type inference.
 * - Provide `validate` argument to handle each value error checking, if its empty no validation will occur.
 *
 * Usage:
 * - Use `form` to access form state, error and modifiers:
 *   - `form.getValues()` -> get current form values
 *   - `form.setValues()` -> set form values
 *   - `form.errors`      -> get form errors
 *   - `form.validate()`  -> check wether current form is valid (based on `validate` argument)
 * - Use `register` (optional) to control the form input
 * - Use `onSubmit` to validate current values and perform form action.
 * - Use `validateMode: onSubmit` to perform validation on submit
 * - Use `validateMode: onBlur` to perform validation in background, whenever value change
 *
 * @example
 *
 * ```tsx
 *  // Access form hook
 *   const { form, register, onSubmit } = useForm<MyFormValues>({
 *      defaultValues: {
 *        name: '',
 *        ...initialData,
 *      },
 *      validate(values, setError) {
 *        if (!values.name) {
 *          setError('name', 'Please enter name!')
 *        }
 *      }
 *  })
 *
 *  // Validate data for submit action
 *  const handleSubmit = onSubmit(
 *    (data, e?) => {
 *    // perform action with data
 *    },
 *    (errors, e?) => {
 *    // If validation fails, the errors will be thrown here
 *    }
 *  )
 * ```
 */
export const useForm = <R extends StateRecord>({
  defaultValues,
  validate,
  validateMode = 'onSubmit',
}: UseFormOptions<R>): UseFormResult<R> => {
  const [state, dispatch] = useReducer(
    (state: FormState<R>, action: FormActions<R>) => formReducer(state, action, validate),
    {
      values: defaultValues,
      errors: undefined,
      isValidating: false,
    },
    createInitialState
  )

  let timeoutRef = useRef<NodeJS.Timeout | undefined>(undefined)

  let currentValueRef = useRef<R | null>(null)

  // Updated validation function to handle async validation
  const performValidation = useCallback(
    async (field?: keyof R, prevErrors?: FormErrors<R>) => {
      if (!validate) return undefined

      let errors: FormErrors<R> = prevErrors ? { ...prevErrors } : {}

      dispatch({ type: 'set-validating', payload: true })

      const currentValue = currentValueRef.current ?? state.values

      try {
        // Create a setError function that updates the errors object
        const setError: FormSetErrorHandler<R> = async (key, errValue: any) => {
          let newError = isEmptyError(errValue) ? undefined : errValue

          if (field) {
            if (field !== key) {
              // noop
            } else {
              if (errValue) {
                errors[key] = newError
              } else {
                delete errors[key]
              }
            }
          } else {
            errors[key] = newError
          }

          // clean empty error
          if (isEmptyError(errors[key])) {
            delete errors[key]
          }
        }

        // Wait for validation to complete
        await validate(currentValue, setError)

        currentValueRef.current = null

        return isEmptyError(errors) ? undefined : errors
      } finally {
        dispatch({ type: 'set-validating', payload: false })
      }
    },
    [state.values, validate]
  )

  // perform validation everytime the value changes
  // wrapped in debounce to optimize the render
  const watchAndValidate = useCallback(
    (field?: keyof R) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }

      timeoutRef.current = setTimeout(async () => {
        if (validateMode === 'onBlur') {
          const errors = await performValidation(field, state.errors)
          dispatch({ type: 'update-errors', payload: errors })
        }
        if (validateMode === 'onSubmit') {
          // For onSubmit mode, we just clear the errors without validation
          dispatch({ type: 'clear-errors', payload: { field } })
        }
      }, 500)
    },
    [performValidation, state.errors, validateMode]
  )

  // form state & controls
  const form = useMemo(
    () => ({
      errors: state.errors,
      isValidating: state.isValidating,
      getValues: (key?: keyof R) => {
        if (!!key) return state.values[key]
        return state.values
      },
      setValues: <K extends keyof R>(arg1: R | K | ((prev: R) => R), arg2?: ((prev: R[K]) => R[K]) | R[K]) => {
        if (typeof arg1 === 'function') {
          // update using prev callback
          dispatch({
            type: 'update-values',
            payload: {
              ...state.values,
              ...arg1(state.values),
            },
          })
          watchAndValidate()
        }
        // direct update by store values
        else if (typeof arg1 === 'object') {
          currentValueRef.current = arg1
          dispatch({
            type: 'update-values',
            payload: arg1,
          })
          watchAndValidate()
        }
        // partial update of each store value keys
        else {
          // update with prev callback
          if (isFunction(arg2)) {
            let currentValue = state.values[arg1]
            dispatch({
              type: 'update',
              payload: {
                field: arg1 as string,
                value: arg2(currentValue),
              },
            })
          } else {
            // direct update
            dispatch({
              type: 'update',
              payload: {
                field: arg1 as string,
                value: arg2,
              },
            })
          }

          watchAndValidate(arg1)
        }
      },
      validate: async <K extends keyof R>(field?: R[K]) => {
        const errors = await performValidation(field)
        dispatch({ type: 'update-errors', payload: errors })
        return isEmpty(errors)
      },
      reset: <K extends keyof R>(key?: R[K]) => {
        dispatch({
          type: 'reset',
          payload: {
            defaultValues,
            field: key,
          },
        })
      },
    }),
    [defaultValues, state.errors, state.values, state.isValidating, watchAndValidate, performValidation]
  ) as UseFormValue<R>

  // form register
  const register = useCallback(
    (fieldName: keyof R) => {
      return {
        name: fieldName,
        value: state.values[fieldName],
        onChange: (e: React.ChangeEvent<any>) => {
          dispatch({
            type: 'update',
            payload: {
              field: fieldName as string,
              value: e.target.value,
            },
          })
          watchAndValidate(fieldName)
        },
      }
    },
    [state.values, watchAndValidate]
  ) as UseFormRegister<R>

  // form submit handler
  const onSubmit: UseFormSubmitHandler<R> = useCallback(
    (onValid, onInvalid) => async (e) => {
      if (e) {
        e.preventDefault()
        e.persist()
      }

      const errors = await performValidation()
      dispatch({ type: 'update-errors', payload: errors })

      if (!!errors) {
        await onInvalid?.(errors, e)
      } else {
        await onValid(state.values, e)
      }
    },
    [performValidation, state.values]
  )

  // cleanup
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  //
  return {
    form,
    register,
    onSubmit,
  }
}
